/**
 * Bookd - Talent Marketplace Platform
 * Copyright © 2025 Bookd. All rights reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL
 * Unauthorized copying, modification, distribution, or use of this software,
 * via any medium, is strictly prohibited without express written permission.
 * 
 * Website: https://alwaysbookd.com
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useBook } from '../../context/BookContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { 
  Sun, Moon, Menu, X, User, Settings, Calendar, LogOut, 
  Briefcase, Sparkles, Search, MessageSquare, CalendarDays, Shield, BookOpen, Heart,
  ArrowLeftRight, Home
} from 'lucide-react';
import { messageApi, roleApi } from '../../lib/api';
import { toast } from 'sonner';

const Navbar = () => {
  const { user, logout, isAuthenticated, updateUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { bookCount, showBookHighlight } = useBook();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Check if user has both roles
  const roles = user?.roles || [];
  const hasBothRoles = roles.includes('booker') && roles.includes('bookee');
  const activeRole = user?.active_role || (user?.user_type === 'talent' ? 'bookee' : 'booker');

  const loadUnreadCount = async () => {
    try {
      const response = await messageApi.getUnreadCount();
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadUnreadCount();
      const interval = setInterval(loadUnreadCount, 30000); // Poll every 30s
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);
  
  const handleSwitchRole = async () => {
    const newRole = activeRole === 'booker' ? 'bookee' : 'booker';
    try {
      const response = await roleApi.switchRole(newRole);
      updateUser({ ...user, active_role: response.data.active_role });
      toast.success(`Switched to ${newRole === 'bookee' ? 'Bookee' : 'Booker'} view`);
    } catch (error) {
      toast.error('Failed to switch role');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/browse', label: 'Browse Talents' },
    { href: '/opportunities', label: 'Opportunities' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-panel" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img 
              src="/bookd-logo-b-only.png" 
              alt="Bookd" 
              className="h-9 w-auto"
            />
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
              Bookd
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  isActive(link.href) ? 'text-primary nav-link-active' : 'text-muted-foreground'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {/* Home Button - visible on all screen sizes */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              className="h-9 w-9"
              data-testid="nav-home-btn"
            >
              <Home className="h-5 w-5" />
            </Button>
            
            {/* Search Button - visible on all screen sizes */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/browse')}
              className="h-9 w-9"
              data-testid="nav-search-btn"
            >
              <Search className="h-5 w-5" />
            </Button>

            {/* Book (Cart) Button - Only for Bookers */}
            {isAuthenticated && user?.user_type === 'client' && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/my-book')}
                className={`relative transition-all duration-300 ${
                  showBookHighlight 
                    ? 'ring-2 ring-amber-500 ring-offset-2 ring-offset-background bg-amber-500/20' 
                    : ''
                }`}
                data-testid="nav-book-btn"
              >
                <BookOpen className={`h-5 w-5 ${showBookHighlight ? 'text-amber-500' : ''}`} />
                {bookCount > 0 && (
                  <Badge className={`absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs ${
                    showBookHighlight ? 'bg-amber-500 animate-pulse' : 'bg-primary'
                  }`}>
                    {bookCount > 9 ? '9+' : bookCount}
                  </Badge>
                )}
              </Button>
            )}

            {/* Messages Button (when authenticated) */}
            {isAuthenticated && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/messages')}
                className="relative"
                data-testid="nav-messages-btn"
              >
                <MessageSquare className="h-5 w-5" />
                {unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-primary">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
              </Button>
            )}

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-9 w-9 rounded-full" data-testid="user-menu-btn">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  {hasBothRoles && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleSwitchRole} data-testid="menu-switch-role">
                        <ArrowLeftRight className="mr-2 h-4 w-4 text-primary" />
                        Switch to {activeRole === 'booker' ? 'Bookee' : 'Booker'}
                        <Badge className="ml-auto text-xs" variant="outline">
                          {activeRole === 'booker' ? 'Booker' : 'Bookee'}
                        </Badge>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    <User className="mr-2 h-4 w-4" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/messages')} data-testid="menu-messages">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Messages
                    {unreadCount > 0 && (
                      <Badge className="ml-auto bg-primary text-primary-foreground text-xs">
                        {unreadCount}
                      </Badge>
                    )}
                  </DropdownMenuItem>
                  {(activeRole === 'booker' || user?.user_type === 'client') && (
                    <DropdownMenuItem onClick={() => navigate('/my-book')} data-testid="menu-book">
                      <BookOpen className="mr-2 h-4 w-4 text-amber-500" />
                      My Book
                      {bookCount > 0 && (
                        <Badge className="ml-auto bg-amber-500">{bookCount}</Badge>
                      )}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/bookings')} data-testid="menu-bookings">
                    <Calendar className="mr-2 h-4 w-4" />
                    My Bookings
                  </DropdownMenuItem>
                  {(activeRole === 'booker' || user?.user_type === 'client') && (
                    <DropdownMenuItem onClick={() => navigate('/favorites')} data-testid="menu-favorites">
                      <Heart className="mr-2 h-4 w-4 text-red-500" />
                      My Favorites
                    </DropdownMenuItem>
                  )}
                  {(activeRole === 'bookee' || user?.user_type === 'talent') && (
                    <DropdownMenuItem onClick={() => navigate('/my-services')} data-testid="menu-services">
                      <Briefcase className="mr-2 h-4 w-4" />
                      My Services
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/calendar')} data-testid="menu-calendar">
                    <CalendarDays className="mr-2 h-4 w-4" />
                    My Calendar
                  </DropdownMenuItem>
                  {user?.is_admin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                      <Shield className="mr-2 h-4 w-4 text-purple-400" />
                      Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden md:flex items-center gap-3">
                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="nav-login-btn">
                  Log in
                </Button>
                <Button 
                  onClick={() => navigate('/register')} 
                  className="rounded-full btn-glow"
                  data-testid="nav-signup-btn"
                >
                  Sign up
                </Button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden mobile-menu-overlay fixed inset-0 top-16 z-40">
          <div className="px-6 py-8 space-y-6">
            {navLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className="block text-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            {!isAuthenticated && (
              <div className="pt-6 space-y-3 border-t border-border">
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                >
                  Log in
                </Button>
                <Button 
                  className="w-full rounded-full" 
                  onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                >
                  Sign up
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
